import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/auth';

export interface TryOnOutfitUpload {
  id: string;
  url: string;
  filename: string;
  createdAt: string;
  size: number;
}

interface UseTryOnOutfitUploadsReturn {
  outfits: TryOnOutfitUpload[];
  loading: boolean;
  error: string | null;
  uploadOutfit: (file: File) => Promise<TryOnOutfitUpload | null>;
  deleteOutfit: (filename: string) => Promise<boolean>;
  refreshOutfits: () => Promise<void>;
}

// Function to safely encode a file name for URLs (borrowed from useDiscoverImages)
const encodeFileName = (filename: string): string => {
  // Check if the filename already contains encoded characters like %20
  const hasEncodedChars = /%[0-9A-F]{2}/.test(filename);
  
  if (hasEncodedChars) {
    try {
      const decodedFilename = decodeURIComponent(filename);
      return encodeURIComponent(decodedFilename);
    } catch (e) {
      const segments = filename.split('/');
      const encodedSegments = segments.map(segment => encodeURIComponent(segment));
      return encodedSegments.join('/');
    }
  } else {
    const segments = filename.split('/');
    const encodedSegments = segments.map(segment => encodeURIComponent(segment));
    return encodedSegments.join('/');
  }
};

// Function to fix a Supabase URL that might contain special characters
const fixSupabaseUrl = (url: string, filename: string): string => {
  // Check if the filename contains characters that need special handling
  if (filename.includes('%') || filename.includes('?') || 
      filename.includes('&') || filename.includes('#') || 
      filename.includes('+') || filename.includes(' ')) {
    
    // Extract the base URL and the path
    const urlParts = url.split('/object/public/');
    if (urlParts.length === 2) {
      const baseUrl = urlParts[0];
      const pathParts = urlParts[1].split('/');
      const bucket = pathParts[0];
      const path = pathParts.slice(1).join('/');
      
      // Rebuild the URL with proper encoding
      return `${baseUrl}/object/public/${bucket}/${encodeFileName(path)}`;
    }
  }
  
  return url;
};

export function useTryOnOutfitUploads(): UseTryOnOutfitUploadsReturn {
  const [outfits, setOutfits] = useState<TryOnOutfitUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { session } = useAuthContext();
  const userId = session?.user?.id;

  const fetchOutfits = useCallback(async () => {
    if (!userId) {
      setOutfits([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: listError } = await supabase.storage
        .from('user-uploaded-try-on-outfits')
        .list(userId, {
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (listError) {
        throw listError;
      }

      if (!data) {
        setOutfits([]);
        return;
      }

      // Process the file data
      const processedOutfits = await Promise.all(
        data.map(async (item) => {
          // Use signed URLs similar to useUserUploads
          const { data: signedData, error: signedError } = await supabase.storage
            .from('user-uploaded-try-on-outfits')
            .createSignedUrl(`${userId}/${item.name}`, 60 * 60); // 1 hour expiration
            
          if (signedError) {
            console.error('Error creating signed URL:', signedError);
            // Fallback to public URL with proper encoding
            const { data: urlData } = supabase.storage
              .from('user-uploaded-try-on-outfits')
              .getPublicUrl(`${userId}/${item.name}`);
                
            // Fix URL if it contains special characters
            const fixedUrl = fixSupabaseUrl(urlData.publicUrl, item.name);
                
            return {
              id: `${userId}/${item.name}`,
              url: fixedUrl,
              filename: item.name,
              createdAt: item.created_at,
              size: item.metadata?.size || 0,
            };
          }
          
          return {
            id: `${userId}/${item.name}`,
            url: signedData.signedUrl,
            filename: item.name,
            createdAt: item.created_at,
            size: item.metadata?.size || 0,
          };
        })
      );

      setOutfits(processedOutfits);
    } catch (err) {
      console.error('Error fetching try-on outfits:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch try-on outfits');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchOutfits();
  }, [fetchOutfits]);

  const uploadOutfit = async (file: File): Promise<TryOnOutfitUpload | null> => {
    if (!userId) {
      setError('User not authenticated');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare the file path
      const timestamp = new Date().getTime();
      const fileExt = file.name.split('.').pop() || 'jpg';
      const uniqueFileName = `${timestamp}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const fullPath = `${userId}/${uniqueFileName}`;
      
      // Upload the file
      const { error: uploadError } = await supabase.storage
        .from('user-uploaded-try-on-outfits')
        .upload(fullPath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get a signed URL for the uploaded file
      const { data: signedData, error: signedError } = await supabase.storage
        .from('user-uploaded-try-on-outfits')
        .createSignedUrl(fullPath, 60 * 60); // 1 hour expiration
      
      if (signedError) {
        console.error('Error creating signed URL:', signedError);
        // Fallback to public URL with proper encoding
        const { data: urlData } = supabase.storage
          .from('user-uploaded-try-on-outfits')
          .getPublicUrl(fullPath);
          
        // Fix URL if it contains special characters
        const fixedUrl = fixSupabaseUrl(urlData.publicUrl, uniqueFileName);
          
        const newOutfit: TryOnOutfitUpload = {
          id: fullPath,
          url: fixedUrl,
          filename: file.name,
          createdAt: new Date().toISOString(),
          size: file.size,
        };
        
        // Add the new upload to the uploads list
        setOutfits(prev => [newOutfit, ...prev]);
        
        return newOutfit;
      }
      
      const newOutfit: TryOnOutfitUpload = {
        id: fullPath,
        url: signedData.signedUrl,
        filename: file.name,
        createdAt: new Date().toISOString(),
        size: file.size,
      };
      
      // Add the new upload to the uploads list
      setOutfits(prev => [newOutfit, ...prev]);
      
      return newOutfit;
    } catch (err) {
      console.error('Error uploading outfit:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload outfit');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteOutfit = async (filename: string): Promise<boolean> => {
    if (!userId) {
      setError('User not authenticated');
      return false;
    }

    try {
      // Extract the full path if necessary
      const fullPath = filename.includes(userId) ? filename : `${userId}/${filename}`;
      
      // Delete the file
      const { error: deleteError } = await supabase.storage
        .from('user-uploaded-try-on-outfits')
        .remove([fullPath]);

      if (deleteError) {
        throw deleteError;
      }

      // Update the outfits list
      setOutfits(prev => prev.filter(outfit => outfit.id !== fullPath));
      
      return true;
    } catch (err) {
      console.error('Error deleting outfit:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete outfit');
      return false;
    }
  };

  const refreshOutfits = async (): Promise<void> => {
    await fetchOutfits();
  };

  return {
    outfits,
    loading,
    error,
    uploadOutfit,
    deleteOutfit,
    refreshOutfits
  };
} 